import React, { useCallback, useState } from 'react';
import { RequirementsProps } from '../../tools/context';
import { useTranslation } from '../../tools/i18n';
import { Picto } from '../Picto';
import './FAQAccordion.scss';

export interface FAQAccordionItem {
  _key?: string;
  question?: string;
  answer?: string;
  openByDefault?: boolean;
}

export interface FAQAccordionProps {
  items?: FAQAccordionItem[];
  requirements: RequirementsProps;
}

const translateContent = (content: string | undefined, t: (key: string) => string): string => {
  if (!content) return '';
  const regex = /{{t.(.*?)}}/g;
  return content.replace(regex, (_, p1: string) => t(p1));
};

const Renderer = (props: FAQAccordionProps) => {
  const { items = [], requirements: _requirements } = props;
  const { t } = useTranslation();

  // Un seul item ouvert à la fois (accordéon "single open")
  const [expandedKey, setExpandedKey] = useState<string | null>(() => {
    const firstOpenIndex = items.findIndex((item) => item.openByDefault);
    if (firstOpenIndex === -1) return null;
    return items[firstOpenIndex]?._key ?? `item-${firstOpenIndex}`;
  });

  const toggleItem = useCallback((key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  }, []);

  const translate = useCallback(
    (content: string | undefined) => translateContent(content, t),
    [t]
  );

  if (!items.length) return null;

  return (
    <div className="FAQAccordion">
      {items.map((item, index) => {
        const key = item._key ?? `item-${index}`;
        const isExpanded = expandedKey === key;
        const questionText = translate(item.question);
        const answerHtml = translate(item.answer);

        return (
          <div key={key} className="FAQAccordion-item">
            <div className="FAQAccordion-header">
              <button
                type="button"
                className="FAQAccordion-trigger"
                onClick={() => toggleItem(key)}
                aria-expanded={isExpanded}
                aria-controls={`faq-answer-${key}`}
                id={`faq-question-${key}`}
              >
                <span className={`FAQAccordion-icon ${isExpanded ? 'FAQAccordion-iconExpanded' : ''}`}>
                  <Picto iconKey="chevron-down" />
                </span>
                <h3 className="FAQAccordion-question">{questionText}</h3>
              </button>
            </div>
            <div
              id={`faq-answer-${key}`}
              role="region"
              aria-labelledby={`faq-question-${key}`}
              className={`FAQAccordion-answer ${isExpanded ? 'FAQAccordion-answerVisible' : ''}`}
              dangerouslySetInnerHTML={{ __html: answerHtml }}
            />
          </div>
        );
      })}
    </div>
  );
};

const FAQAccordion = { Renderer };

export default FAQAccordion;
export { Renderer };
